import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from './ui/dialog';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = (window.BACKEND_URL || process.env.REACT_APP_BACKEND_URL || "");
const API = `${BACKEND_URL}/api`;

/**
 * Select parametrizado que carga opciones desde /api/catalogs/{type}
 * y permite agregar nuevas con un botón "+ Agregar".
 *
 * Props:
 *  - catalogType: "teams" | "medical_coverages" | "nationalities" | "positions" | "shoe_sizes"
 *  - value: valor actual
 *  - onChange(value)
 *  - placeholder
 *  - disabled
 *  - allowAdd: boolean (default true) - si false sólo muestra el select sin el "+"
 *  - testId: data-testid para el select
 *  - addLabel: texto del botón agregar (opcional, default "Agregar nuevo")
 *  - className
 */
const SelectWithAdd = ({
  catalogType,
  value,
  onChange,
  placeholder = 'Seleccionar...',
  disabled = false,
  allowAdd = true,
  testId,
  addLabel,
  className = '',
  includeEmpty = false,
  emptyLabel = '— Sin especificar —',
}) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newValue, setNewValue] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const fetchOptions = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/catalogs/${catalogType}`);
      if (mountedRef.current) setOptions(res.data || []);
    } catch (e) {
      console.error(`Error cargando catálogo ${catalogType}:`, e);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    fetchOptions();
    // eslint-disable-next-line
  }, [catalogType]);

  const handleAdd = async () => {
    const trimmed = newValue.trim();
    if (!trimmed) {
      toast.error('Ingresá un valor');
      return;
    }
    try {
      setSubmitting(true);
      const res = await axios.post(`${API}/catalogs/${catalogType}`, { value: trimmed });
      const added = res.data.value;
      await fetchOptions();
      onChange?.(added);
      toast.success(`"${added}" agregado al catálogo`);
      setShowAddDialog(false);
      setNewValue('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al agregar');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className={`flex gap-2 ${className}`}>
        <Select
          value={value || (includeEmpty ? '__empty__' : undefined)}
          onValueChange={(v) => onChange?.(v === '__empty__' ? '' : v)}
          disabled={disabled || loading}
        >
          <SelectTrigger data-testid={testId} className="flex-1">
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>
          <SelectContent>
            {includeEmpty && <SelectItem value="__empty__">{emptyLabel}</SelectItem>}
            {options.map((opt) => (
              <SelectItem key={opt} value={opt}>{opt}</SelectItem>
            ))}
            {options.length === 0 && !loading && (
              <div className="p-2 text-xs text-gray-500 text-center">
                Sin opciones aún
              </div>
            )}
          </SelectContent>
        </Select>
        {allowAdd && !disabled && (
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={() => setShowAddDialog(true)}
            data-testid={testId ? `${testId}-add` : undefined}
            title="Agregar nuevo"
            className="shrink-0"
          >
            <Plus className="w-4 h-4" />
          </Button>
        )}
      </div>

      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{addLabel || 'Agregar nuevo'}</DialogTitle>
            <DialogDescription>
              El valor quedará disponible en todos los formularios.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="new-catalog-value">Valor</Label>
            <Input
              id="new-catalog-value"
              data-testid="catalog-add-input"
              value={newValue}
              onChange={(e) => setNewValue(e.target.value)}
              placeholder="Escribí el valor nuevo..."
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAdd();
                }
              }}
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => { setShowAddDialog(false); setNewValue(''); }}
              disabled={submitting}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              onClick={handleAdd}
              disabled={submitting || !newValue.trim()}
              className="bg-brand-600 hover:bg-brand-700"
              data-testid="catalog-add-confirm"
            >
              {submitting ? 'Agregando...' : 'Agregar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SelectWithAdd;
